/**
 * MUS-505 - what a sight-reading lesson or a repertoire piece does when the
 * player is taken away from it.
 *
 * A session that is playing is paused the moment the player is taken away: the
 * window loses focus, the page is hidden (tab switched, window minimised, app
 * in the background, device locked or asleep) or the native app stops being
 * active (notification shade, control centre, app switcher, an incoming call).
 * Coming back never starts it again. The player is not at the keyboard at the
 * instant the page reappears, so only their own play control may start it. A
 * session the player had paused themselves is not touched at all.
 *
 * The first MUS-505 fix re-armed playback on every return. A hidden page runs
 * no animation frames, so the scene's pause was still waiting for one when the
 * re-arm arrived; the pause then ran after it and left the lesson showing
 * "playing" over a scene that had stopped updating.
 *
 * The browser and the native shell give no fixed order for these events, and
 * any of them can arrive twice. Pausing is safe to repeat, and the return only
 * reports, once, that a session paused here is back on screen.
 *
 * The side effects are injected so the event orders can be unit tested without
 * rendering a play surface.
 */

export interface InterruptionPauseDeps {
  /** True while the session is playing. */
  isPlaying: () => boolean;
  /** True while the page is hidden. */
  isPageHidden: () => boolean;
  /** Pause the session. Only called while it is playing. */
  pause: () => void;
  /**
   * Optional: told once when a session paused here is back on screen. It must
   * not start playback.
   */
  onReturn?: () => void;
}

/** Handlers for window blur, document visibilitychange and the native app state. */
export interface InterruptionPause {
  onBlur: () => void;
  onVisibilityChange: () => void;
  onAppStateChange: (state: { isActive: boolean }) => void;
}

export function makeInterruptionPause(deps: InterruptionPauseDeps): InterruptionPause {
  // True from a pause made here until the session is back on screen.
  let pausedHere = false;

  const goingAway = () => {
    if (!deps.isPlaying()) return;
    pausedHere = true;
    deps.pause();
  };

  const comingBack = () => {
    // An active app can still have a hidden page; the player is not back until
    // the page is visible.
    if (deps.isPageHidden() || !pausedHere) return;
    pausedHere = false;
    deps.onReturn?.();
  };

  return {
    onBlur: goingAway,
    onVisibilityChange: () => (deps.isPageHidden() ? goingAway() : comingBack()),
    onAppStateChange: ({ isActive }) => (isActive ? comingBack() : goingAway()),
  };
}

export interface AudioClockDeps {
  /** Current audio-clock state ('suspended' | 'running' | 'closed', or WebKit's 'interrupted'). */
  state: () => string;
  /** Ask the audio clock to run. */
  resume: () => Promise<void>;
  /** Optional error sink for a failed resume; never throws to the caller. */
  onError?: (e: unknown) => void;
}

/**
 * The browser can stop the audio clock while the page is away ('suspended', or
 * 'interrupted' in WebKit). Playback is timed off that clock, so a session
 * started on a stopped clock shows "playing" and never moves. Call this
 * synchronously from the player's own play action: the browser only lets the
 * clock run again inside a user gesture, and `resume` is invoked before this
 * returns.
 */
export function resumeAudioClockIfStopped(deps: AudioClockDeps): void {
  const state = deps.state();
  if (state === 'running' || state === 'closed') return;
  try {
    deps.resume().catch((e: unknown) => deps.onError?.(e));
  } catch (e) {
    deps.onError?.(e);
  }
}
